import React, { useState, useEffect } from "react";
import { Box, Paper, TextField, Button, Typography, Alert, Container } from "@mui/material";
import { signInWithEmailAndPassword } from "firebase/auth";
import { FirebaseError } from "firebase/app";
import { auth } from "../services/firebase";
import Logger from "../utils/logger";

const Login: React.FC = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [errorMessage, setErrorMessage] = useState<string>("");
  const [loading, setLoading] = useState(false);

  // AuthContextは使わず、直接Firebase認証を呼ぶ

  useEffect(() => {
    Logger.debug("errorMessage state changed:", errorMessage);
    if (errorMessage) {
      Logger.debug("ERROR MESSAGE IS SET:", errorMessage);
    }
  }, [errorMessage]);

  const getJapaneseErrorMessage = (errorCode: string): string => {
    switch (errorCode) {
      case "auth/user-not-found":
        return "このメールアドレスは登録されていません";
      case "auth/wrong-password":
        return "パスワードが間違っています";
      case "auth/invalid-email":
        return "メールアドレスの形式が正しくありません";
      case "auth/user-disabled":
        return "このアカウントは無効化されています";
      case "auth/too-many-requests":
        return "ログイン試行回数が多すぎます。しばらく待ってから再試行してください";
      case "auth/network-request-failed":
        return "ネットワークエラーが発生しました。接続を確認してください";
      case "auth/invalid-credential":
        return "メールアドレスまたはパスワードが間違っています";
      case "auth/configuration-not-found":
        return "Firebase設定エラー。エミュレータが起動していない可能性があります";
      case "auth/emulator-config-failed":
        return "エミュレータ接続に失敗しました。エミュレータが起動していることを確認してください";
      case "unknown":
        return "ログインエラーが発生しました。詳細はコンソールを確認してください";
      default:
        return `ログインに失敗しました (${errorCode})`;
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrorMessage(""); // エラーをクリア
    setLoading(true);

    Logger.info("🔐 Login form submitted");
    Logger.debug("📧 Email:", email);
    Logger.debug("🔑 Password length:", password.length);
    Logger.debug("🔥 Auth instance:", auth);
    Logger.debug("🌐 Auth config:", {
      apiKey: auth.config.apiKey?.substring(0, 10) + "...",
      authDomain: auth.config.authDomain,
    });

    try {
      Logger.debug("📤 Calling signInWithEmailAndPassword...");
      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      Logger.info("✅ Login successful!", { uid: userCredential.user.uid });
      Logger.debug("📧 User email:", userCredential.user.email);
      // 成功時はAuthContextのonAuthStateChangedが自動的に状態を更新する
    } catch (err) {
      Logger.error("❌ Login failed", err);
      const errorCode = err instanceof FirebaseError ? err.code : "unknown";
      const japaneseErrorMessage = getJapaneseErrorMessage(errorCode);
      Logger.debug("Setting error message:", japaneseErrorMessage);
      setErrorMessage(japaneseErrorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container component="main" maxWidth="sm">
      <Box
        sx={{
          marginTop: 8,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
        }}
      >
        <Paper elevation={3} sx={{ padding: 4, width: "100%" }}>
          <Typography component="h1" variant="h4" align="center" gutterBottom>
            リプレン管理画面
          </Typography>

          <Typography variant="body1" align="center" color="textSecondary" gutterBottom>
            管理者としてログインしてください
          </Typography>

          {errorMessage !== "" && (
            <Alert
              severity="error"
              sx={{
                mt: 2,
                mb: 2,
                "& .MuiAlert-message": {
                  fontSize: "1rem",
                  fontWeight: "bold",
                },
              }}
            >
              {errorMessage}
            </Alert>
          )}

          <Box component="form" sx={{ mt: 3 }}>
            <TextField
              margin="normal"
              required
              fullWidth
              id="email"
              label="メールアドレス"
              name="email"
              autoComplete="email"
              autoFocus
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              disabled={loading}
            />
            <TextField
              margin="normal"
              required
              fullWidth
              name="password"
              label="パスワード"
              type="password"
              id="password"
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              disabled={loading}
            />
            <Button
              type="button"
              fullWidth
              variant="contained"
              sx={{ mt: 3, mb: 2 }}
              disabled={loading}
              onClick={handleSubmit}
            >
              {loading ? "ログイン中..." : "ログイン"}
            </Button>
          </Box>
        </Paper>
      </Box>
    </Container>
  );
};

export default Login;
